import {
  connectionToolListResponseSchema,
  type ConnectionToolListResponse,
} from '@coworker/contracts';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '../auth/AuthProvider.js';
import { ApiError } from '../lib/api-error.js';
import { useCurrentWorkspace } from './use-current-workspace.js';
import { useRecoverSession } from './use-recover-session.js';

/**
 * Which of an app's tools a connection may use - COW-493, AGT-006.
 *
 * Keyed by connection as well as by workspace, the shape `useMessages` uses for
 * a thread: two accounts of the same app are two different answers, and INT-008
 * makes that an ordinary case rather than an edge one.
 */
export const connectionToolsQueryKey = (
  workspaceId: string | undefined,
  connectionId: string | undefined,
) => ['connection-tools', workspaceId, connectionId] as const;

export interface ConnectionToolsState {
  readonly query: UseQueryResult<ConnectionToolListResponse, unknown>;
  readonly isUnauthenticated: boolean;
}

/**
 * Reads the picker's contents.
 *
 * `enabled` is gated on the connection id as well as the workspace: the panel
 * is opened per row, and asking before one is chosen would send a request that
 * comes back 400 - which reads as a failure when the truth is there is nothing
 * to ask about yet.
 */
export function useConnectionTools(connectionId: string | undefined): ConnectionToolsState {
  const { api, status } = useAuth();
  const workspace = useCurrentWorkspace();

  const query = useQuery({
    queryKey: connectionToolsQueryKey(workspace?.id, connectionId),
    enabled: status === 'signedIn' && workspace !== undefined && connectionId !== undefined,
    queryFn: () =>
      api.request(`/v1/connections/${connectionId ?? ''}/tools`, {
        schema: connectionToolListResponseSchema,
        workspaceId: workspace?.id ?? '',
      }),
  });

  const isUnauthenticated = query.error instanceof ApiError && query.error.isUnauthenticated;
  useRecoverSession({ isUnauthenticated, erroredAt: query.errorUpdatedAt });

  return { query, isUnauthenticated };
}

/**
 * Replaces the switched-off set - the whole set, never a delta.
 *
 * `POST` rather than `PUT`: nothing in this API uses `PUT` and `ApiClient` has
 * no such verb, so adding one buys nothing that `POST /:id/status` does not
 * already do. Not because the gateway would need changing - it would not,
 * `PUT` is routed and allowed there already.
 *
 * The response *is* the new state, so it is written straight into the cache
 * rather than invalidated: a refetch would show the old answer for as long as
 * the round trip takes, and a picker that visibly reverts for half a second is
 * how somebody clicks twice.
 */
export function useSetConnectionTools(connectionId: string | undefined) {
  const { api } = useAuth();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (disabled: readonly string[]) =>
      api.request(`/v1/connections/${connectionId ?? ''}/tools`, {
        method: 'POST',
        schema: connectionToolListResponseSchema,
        workspaceId: workspace?.id ?? '',
        body: { disabled },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(connectionToolsQueryKey(workspace?.id, connectionId), data);
    },
  });
}
